import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus } from 'lucide-react';
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/formValidation';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { signUp } = useAuth();

  const runValidation = () => {
    const emailRes = validateEmail(email);
    const passRes = validatePassword(password);
    const confirmRes = validateConfirmPassword(password, confirmPassword);
    const errs: Record<string, string> = {};
    if (!emailRes.valid) errs.email = emailRes.message;
    if (!passRes.valid) errs.password = passRes.message;
    if (!confirmRes.valid) errs.confirmPassword = confirmRes.message;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleBlur = (field: string) => {
    setTouched((p) => ({ ...p, [field]: true }));
    if (field === 'email') {
      const res = validateEmail(email);
      setFieldErrors((p) => ({ ...p, email: res.valid ? '' : res.message }));
    }
    if (field === 'password') {
      const res = validatePassword(password);
      setFieldErrors((p) => ({ ...p, password: res.valid ? '' : res.message }));
      const confirmRes = validateConfirmPassword(password, confirmPassword);
      setFieldErrors((p) => ({ ...p, confirmPassword: confirmRes.valid ? '' : confirmRes.message }));
    }
    if (field === 'confirmPassword') {
      const res = validateConfirmPassword(password, confirmPassword);
      setFieldErrors((p) => ({ ...p, confirmPassword: res.valid ? '' : res.message }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!runValidation()) return;

    setLoading(true);

    const { error } = await signUp(email, password);
    
    if (error) {
      setError(error.message || 'יצירת החשבון נכשלה');
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-black rounded-lg mb-4">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">צרו חשבון חדש</h1>
          <p className="text-gray-600">הרשמו כדי להתחיל</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800 font-medium mb-1">ההרשמה בוצעה בהצלחה!</p>
              <p className="text-sm text-green-700">
                אנא בדקו את האימייל שלכם לאימות החשבון.
              </p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              אימייל
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur('email')}
              required
              disabled={success}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed min-h-[44px] text-base ${
                touched.email && fieldErrors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="you@example.com"
            />
            {touched.email && fieldErrors.email && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
              סיסמה
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              required
              disabled={success}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed min-h-[44px] text-base ${
                touched.password && fieldErrors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            {touched.password && fieldErrors.password && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
              אימות סיסמה
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => handleBlur('confirmPassword')}
              required
              disabled={success}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed min-h-[44px] text-base ${
                touched.confirmPassword && fieldErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            {touched.confirmPassword && fieldErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={
              loading ||
              success ||
              !validateEmail(email).valid ||
              !validatePassword(password).valid ||
              !validateConfirmPassword(password, confirmPassword).valid
            }
            className="w-full bg-black text-white py-2.5 px-4 rounded-md font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] text-base"
          >
            {loading ? 'יוצר חשבון...' : success ? 'החשבון נוצר' : 'הרשמה'}
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              כבר יש לכם חשבון?{' '}
              <Link to="/login" className="text-black font-medium hover:underline">
                התחברות
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
